import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";
import "./TrackProgressCard.css";

export default function TrackProgressCard() {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [enrRes, sugRes] = await Promise.all([
          api.get("/tracks/my-enrollments").catch(() => ({ data: [] })),
          api.get("/tracks/suggestions").catch(() => ({ data: null }))
        ]);
        setEnrollments(Array.isArray(enrRes.data) ? enrRes.data : []);
        setSuggestions(sugRes.data);
      } catch {
        setEnrollments([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return null;

  return (
    <div className="tpc-container">
      <div className="tpc-header">
        <div className="tpc-header-left">
          <h2 className="tpc-title">My Learning Tracks</h2>
          <span className="tpc-subtitle">Your structured path to mastery</span>
        </div>
        <button className="tpc-browse-btn" onClick={() => navigate("/tracks")}>
          Browse Tracks →
        </button>
      </div>

      {enrollments.length === 0 ? (
        <div className="tpc-empty">
          <div className="tpc-empty-icon">🗺️</div>
          <h3>No tracks enrolled yet</h3>
          <p>
            Tracks are structured learning paths with phases, projects, and assessments —
            designed to take you from beginner to job-ready.
          </p>
          <button className="tpc-start-btn" onClick={() => navigate("/tracks")}>
            Explore Tracks →
          </button>
        </div>
      ) : (
        <div className="tpc-grid">
          {enrollments.slice(0, 3).map((enrollment) => {
            const track = enrollment.track;
            if (!track) return null;

            const totalPhases = track.phaseCount || 1;
            // eslint-disable-next-line no-unused-vars
            const completedPhases = enrollment.completedPhaseCount || 0;
            const progressPct = enrollment.progressPercent || 0;
            const currentPhaseNum = enrollment.currentPhase || 1;
            const isCompleted = enrollment.completed;

            return (
              <div
                key={enrollment._id}
                className="tpc-card"
                onClick={(e) => {
                  if (e.target.closest('.tpc-share-btn')) return;
                  navigate(`/tracks/${track.slug}`);
                }}
              >
                {track.thumbnail && (
                  <div className="tpc-card-thumb">
                    <img
                      src={track.thumbnail}
                      alt={track.title}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                    <div className="tpc-card-overlay" />
                  </div>
                )}
                <div className="tpc-card-body">
                  <h3 className="tpc-card-title">{track.title}</h3>
                  <span className="tpc-card-phase">
                    {isCompleted ? "Completed 🎉" : `Phase ${Math.min(currentPhaseNum, totalPhases)} of ${totalPhases}`}
                  </span>
                  <div className="tpc-progress-track">
                    <div className="tpc-progress-fill" style={{ width: `${progressPct}%` }} />
                  </div>
                  <div className="tpc-card-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span className="tpc-pct">{progressPct}%</span>
                      {enrollment.readinessScore > 0 && (
                        <span className="tpc-readiness">
                          Readiness: {enrollment.readinessScore}%
                        </span>
                      )}
                    </div>
                    {isCompleted && (
                      <button
                        className="tpc-share-btn"
                        style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', backgroundColor: 'var(--accent-light)', color: 'var(--accent)', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const profileUrl = `${window.location.origin}/u/${track.slug}`; // fallback
                          try {
                            // Try to get certificate
                            const res = await api.get(`/certificates/${track._id}`);
                            const url = `${window.location.origin}/certificate/${res.data.certificateId}`;
                            if (navigator.share) {
                              await navigator.share({ title: "My Learnlia Certificate", text: `I completed ${track.title} on Learnlia!`, url });
                            } else {
                              navigator.clipboard.writeText(url);
                              alert("Certificate link copied!");
                            }
                          } catch (err) {
                            if (navigator.share) {
                              await navigator.share({ title: "My Learnlia Profile", url: profileUrl });
                            } else {
                              navigator.clipboard.writeText(profileUrl);
                              alert("Profile link copied!");
                            }
                          }
                        }}
                      >
                        Share
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Always show "explore more" tile */}
          <div
            className="tpc-add-tile"
            onClick={() => navigate("/tracks")}
          >
            <span className="tpc-add-tile-icon">+</span>
            Explore more tracks
          </div>
        </div>
      )}

      {/* ── Learnlia Intelligence Suggestions ──────────────────── */}
      {suggestions && suggestions.suggestions?.length > 0 && (
        <div className="tpc-suggestions-block" style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid var(--border)' }}>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '800', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '18px' }}>✨</span> AI Suggested Tracks
            </h3>
            {suggestions.targetRole ? (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
                Based on your goal to become a <strong>{suggestions.targetRole}</strong> by <strong>{suggestions.targetMonth || 'soon'}</strong>.
              </p>
            ) : (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
                Popular tracks to accelerate your learning.
              </p>
            )}
          </div>

          <div className="tpc-grid">
            {suggestions.suggestions.map((track) => (
              <div
                key={track._id}
                className="tpc-card"
                onClick={() => navigate(`/tracks/${track.slug}`)}
                style={{ border: '1px solid var(--accent-light, rgba(37,99,235,0.2))' }}
              >
                {track.thumbnail && (
                  <div className="tpc-card-thumb" style={{ height: '100px' }}>
                    <img
                      src={track.thumbnail}
                      alt={track.title}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                    <div className="tpc-card-overlay" />
                  </div>
                )}
                <div className="tpc-card-body" style={{ padding: '12px' }}>
                  <h3 className="tpc-card-title" style={{ fontSize: '14px' }}>{track.title}</h3>
                  <span className="tpc-card-phase">{track.phaseCount} Phases • {track.estimatedMonths} Months</span>
                  <div style={{ marginTop: '10px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--accent)', background: 'var(--accent-light)', padding: '2px 8px', borderRadius: '4px' }}>
                      View Track
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}